import React, { useState, useEffect, useRef } from 'react';

interface TimerDisplayProps {
  timer: number;
  isActive: boolean;
  gameStatus?: 'active' | 'waiting' | 'finished' | 'paused';
}

export const TimerDisplay: React.FC<TimerDisplayProps> = ({ timer, isActive, gameStatus = 'active' }) => {
  const [displayTime, setDisplayTime] = useState(timer);
  const lastServerTimeRef = useRef(timer);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastUpdateTimeRef = useRef(Date.now());

  useEffect(() => {
    // When server sends new timer value, update our reference
    if (timer !== lastServerTimeRef.current) {
      lastServerTimeRef.current = timer;
      lastUpdateTimeRef.current = Date.now();
      setDisplayTime(timer);
    }
  }, [timer]);

  useEffect(() => {
    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Stop countdown immediately if game has finished
    if (gameStatus === 'finished') {
      // Show the final timer value from server
      setDisplayTime(lastServerTimeRef.current);
      return;
    }

    // Only count down if this timer is active and has time remaining
    if (isActive && lastServerTimeRef.current > 0) {
      // Update every second for smooth countdown
      intervalRef.current = setInterval(() => {
        const now = Date.now();
        const elapsed = Math.floor((now - lastUpdateTimeRef.current) / 1000);
        const newTime = Math.max(0, lastServerTimeRef.current - elapsed);
        setDisplayTime(newTime);
      }, 1000); // Update every second

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      };
    } else {
      // If not active, just show the server time
      setDisplayTime(lastServerTimeRef.current);
    }
  }, [isActive, timer, gameStatus]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div style={{ 
      fontSize: '12px',
      fontWeight: isActive ? 'bold' : 'normal',
      color: isActive ? '#1976d2' : '#666',
      transition: 'color 0.2s ease'
    }}>
      {formatTime(displayTime)}
    </div>
  );
};
